<template>
  <div class="pdf-maker-container">

    <div class="pdf-form" v-if="!proposalLoading">

      <v-alert 
        class="mb-5"
        v-if="!editable"
        type="warning">
        This proposal has been sent to the client and can no longer be modified.
      </v-alert>

      <div class="title is-4">
        Project Information
      </div>

      <v-text-field
        class="field"
        :label="proposal.project_info.name.label"
        :placeholder="proposal.project_info.name.label"
        v-model="proposal.project_info.name.value"
        disabled>
      </v-text-field>

      <v-text-field
        class="field"
        :label="proposal.project_info.address.label"
        :placeholder="proposal.project_info.address.label"
        v-model="proposal.project_info.address.value"
        :disabled="!editable"
      />

      <div class="columns">

        <v-text-field
          class="column"
          :label="proposal.project_info.sales_person.label"
          :placeholder="proposal.project_info.sales_person.label"
          v-model="proposal.project_info.sales_person.value.From"
          disabled
        />

        <v-select
          class="column"
          :disabled="!editable"
          :label="proposal.project_info.contact.label"
          v-bind="proposal.project_info.contact.bind"
          item-value="Email"
          item-text="Name"
          :value="proposal.project_info.contact.value.Email"
          @input="proposal.project_info.contact.value = changeContact(
            proposal.project_info.contact.bind.options, $event
          )"
        />

      </div>

      <div class="field">
        <label class="label">
          {{ proposal.project_info.cover_photo.label }}
        </label>
        <v-drop-zone
          :disabled="!editable"
          height="100px"
          @input="fileInput"
          :file-name.sync="proposal.project_info.cover_photo.value.name"
          :value="proposal.project_info.cover_photo.value.file"
          :id="`proposal-cover-${project.id}`">
        </v-drop-zone>
      </div>

      <div class="field">
        <label class="label">
          {{ proposal.project_info.date.label }}
        </label>
        <v-datepicker
          :disabled="!editable"
          placeholder="Select proposal date"
          v-model="proposal.project_info.date.value">
        </v-datepicker>
      </div>

      <div class="field">
        <label class="label">
          Quote No.
        </label>
        <v-text-field
          :disabled="!editable"
          placeholder="Enter quote no."
          :value="proposal.project_info.contact.value['Quote No.']"
          @input="changedQuoteNo($event)">
        </v-text-field>
      </div>

      <div class="field" v-if="proposal && proposal.project_info.pricing">
        <div>
          <label class="label">
            RFQ
          </label>
          <template v-if="filteredRfqs.length > 0">
            <v-select
              :disabled="!editable"
              :options="filteredRfqs"
              :item-text="v => `${formatDate(parseDate(v.accepted_on))} - 
                ${v.rfq_name} - V${v.version} (${v.created_user.display_name})`"
              item-value="id"
              v-model="proposal.project_info.pricing.rfq_id"
              @input="updatedRfq($event)"
            />
            <span class="help">
              Only RFQs that have completed estimations are listed
            </span>
          </template>

          <v-fade-transition mode="out-in">

            <div 
              v-if="!rfqLoading && proposal.project_info.pricing.rfq_id && rfq && estimation"
              class="mt-3">

              <div class="field">
                <div>
                  <span class="has-text-weight-semibold">
                    Estimation status:
                  </span> 
                  <span class="is-capitalized">
                    {{ estimation.status.toLowerCase() }}
                  </span>
                </div>

                <div>
                  <span class="has-text-weight-semibold">
                    Estimated by:
                  </span> 
                  {{ estimation.created_user.display_name }}
                </div>

                <div>
                  <span class="has-text-weight-semibold">
                    Estimation created on:
                  </span> 
                  {{ formatDate(parseDate(estimation.created_on)) }}
                </div>
              </div>

              <div class="buttons">
                <router-link 
                  class="button is-small is-link"
                  :to="{ 
                    name: 'RfqViewer', 
                    params: { rfqId: rfq.id, rfq: rfq }}">
                  View RFQ
                </router-link>

                <router-link 
                  class="button is-small is-link"
                  :to="{ 
                    name: productBrand == 'Mitrex' ? 'EstimationViewerMitrex' : 
                      'EstimationViewerCladify', 
                    params: { estimationId: estimation.id }}">
                  View Estimation
                </router-link>
              </div>

              <v-alert 
                class="my-3"
                v-if="!estimation.is_completed"
                type="warning">
                The estimation for this RFQ is not yet completed. Please use the
                information with caution.
              </v-alert>

              <div
                class="field" 
                v-if="productBrand == 'Mitrex' && proposal.proposal_type !== CONDENSED">
                <label class="label">Pricing Types Shown</label>
                <div class="control">
                  <v-checkbox 
                    v-model="proposal.project_info.pricing.has_mpa_price"
                    @input="refreshRfq()"
                    :disabled="(proposal.project_info.pricing.has_mpa_price && 
                      !proposal.project_info.pricing.has_dpa_price)"
                    label="MPA Pricing"/>
                  <v-checkbox 
                    v-model="proposal.project_info.pricing.has_dpa_price"
                    @input="refreshRfq()"
                    :disabled="(!proposal.project_info.pricing.has_mpa_price && 
                      proposal.project_info.pricing.has_dpa_price)"
                    label="DPA Pricing"/>
                </div>
              </div>

            </div>

            <div 
              v-if="rfqLoading" 
              class="is-relative mt-3">
              <v-spin-loader :value="true">
                <div class="py-6"></div>
              </v-spin-loader>
            </div>

            <v-alert 
              v-if="!rfqLoading && proposal && proposal.project_info 
                && proposal.project_info.pricing
                && proposal.project_info.pricing.rfq_id && !rfq"
              type="warning"
              class="mt-3">
              The selected RFQ does not exist. It may have been deleted. Please
              select a different RFQ.
            </v-alert>

          </v-fade-transition>
        </div>

        <v-alert 
          class="mt-2"
          v-if="!!project && filteredRfqs.length == 0"
          type="info">
          There are no RFQs with completed estimations for this project. 
          You can still proceed to manually generate information for the 
          proposal by inserting images for pricing
        </v-alert>
      </div>

      <div class="field">
        <label class="label">Proposal Sent On</label>
        <div class="flex flex--row">
          <v-datepicker
            class="flex--grow"
            placeholder="Select date sent to client"
            v-model="proposal.sent_on"
          />
          <button
            class="button"
            @click="proposal.sent_on = null">
            <span class="icon is-medium">
              <i class="fas fa-times"></i>
            </span>
          </button>
        </div>
        <div class="help">
          Note that the proposal can no longer be edited after being sent to a client
        </div>
      </div>

      <!-- ///////////////////////////
      ///// General Information /////
      //////////////////////////////-->

      <div class="title is-4">
        <hr />
        General Information
      </div>

      <template
        v-for="(section, i) in proposal.proposal_info">

        <component
          :key="i"
          v-if="['VDynamicList', 'VRichText'].indexOf(section.f_component) == -1"
          class="field"
          :disabled="
            !editable 
            || (['supply_install', 'quote_type'].includes(section.id) 
            && (proposal.project_info.pricing && !!proposal.project_info.pricing.rfq_id))
          "
          :is="section.f_component"
          :label="section.label"
          v-bind="section.bind"
          v-model="section.value">
        </component>

        <div
          :key="i"
          v-if="section.f_component == 'VDynamicList'"
          class="field">
          <v-label
            :editable="editable"
            :text.sync="section.label"
          />

          <v-dynamic-list
            :disabled="!editable"
            :value="section.value"
            v-bind="section.bind">
          </v-dynamic-list>
        </div>

        <div
          class="field"
          :key="i"
          v-if="section.f_component == 'VRichText'">
          <v-label
            :editable="!editable"
            :text.sync="section.label"/>
          <v-rich-text 
            :disabled="!editable" 
            v-model="section.value"
            :key="richTextRefreshCount"
          />
        </div>

      </template>

      <!-- ///////////////////////////
      ///// Rest of Content ////////
      //////////////////////////////-->

      <div
        v-for="(section, i) in proposal.proposal_content"
        :key="section.name + '-' + i">

        <hr />

        <div class="title is-4">
          {{ section.name }}
        </div>

        <div
          v-if="section.id === 'general_tos'"
          :key="`include-section-${i}`" 
          class="field mb-5">
          <label
            class="checkbox">
            <input
              type="checkbox"
              :disabled="!editable"
              v-model="section.visible">
            Include section
          </label>
        </div>

        <!-- static type sections -->
        <template v-if="section.type == 'static'">

          <div v-for="(subsection, i) in section.subsections"
            class="field"
            :key="`static-subsection-${i}`">

            <v-label
              :editable="editable"
              :text.sync="subsection.label"/>

            <v-multiselect
              v-if="subsection.f_component == 'VMultiselect'"
              :value="subsection.value"
              v-bind="subsection.bind"
              :disabled="!editable"
              class="is-fullwidth"/>

            <v-dynamic-list
              v-if="subsection.f_component == 'VDynamicList'"
              :value="subsection.value"
              :row-template="subsection.template"
              v-bind="subsection.f_bind"
              :disabled="!editable"
              is-column>
            </v-dynamic-list>

            <v-rich-text 
              v-if="subsection.f_component == 'VRichText'"
              v-model="subsection.value" 
              v-bind="subsection.bind"
              :key="richTextRefreshCount"
            />

            <template v-if="subsection.f_component == 'TextAndImageF'">
              <v-text-field
                placeholder="Text description"
                :disabled="!editable"
                v-model="subsection.value.text">
              </v-text-field>

              <v-file-upload
                class="is-fullwidth"
                v-model="subsection.value.image"
                file-type-validator=".*"
                :disabled="!editable"
                has-name>
              </v-file-upload>
            </template>
          </div>

        </template>


        <!-- dynamic type sections: subsections are flexible -->
        <div
          v-if="section.type == 'pricing'">
          <div 
            class="pricing-subsection" 
            v-for="(subsection, i) in section.subsections"
            :key="`pricing-subsection-${i}`">
            <template v-for="subsectionEl in subsection">
              <component
                v-if="subsectionEl.form"
                :key="subsectionEl.id + '-' + i"
                v-bind="subsectionEl.form.bind"
                :disabled="!editable"
                class="field"
                :is="subsectionEl.form.component"
                v-model="subsectionEl.value"
              />
            </template>
          </div>
        </div>

        <!-- dynamic type sections: subsections are flexible -->
        <div v-if="section.type == 'dynamicSubsections'">
          <v-dynamic-list
            :value="section.subsections"
            :row-template="section.subsection_template"
            :disabled="!editable"
            is-column
            v-bind="section.f_bind"
          />
        </div>

        <!-- optional sections -->
        <div v-if="section.type == 'optional'">
          <component
            v-for="(input, index) in section.inputs"
            :key="`optional-section-input-${index}`"
            v-bind="input.bind"
            class="field"
            :is="input.component"
            v-model="section.bind[input.bind_field]">
          </component>

          <div class="field">
            <label
              class="checkbox">
              <input
                type="checkbox"
                :disabled="!editable"
                v-model="section.visible">
              Include section
            </label>
          </div>
        </div>

      </div>

    </div>

    <div class="pdf-preview">
      <template v-if="!proposalLoading && !rfqLoading">
        <condensed-proposal-previewer
          v-if="proposal.proposal_type === CONDENSED"
          :key="`${saves}-condensed`"
          :proposal="proposal"
          :product-brand="productBrand"
          :rfq="rfq"
          :installation-methods="installationMethods"
        />
        <pdf-previewer
          v-else
          :key="`${saves}-full`"
          :proposal="proposal"
          :product-brand="productBrand"
          :rfq="rfq"
          :installation-methods="installationMethods"
        />
      </template>
    </div>

    <v-loading 
      :value="proposalLoading || rfqFirstLoad"
      :message="loadingMessage"
    />

  </div>
</template>

<script>
import PdfPreviewer from '@/components/proposalmaker/PdfPreviewer.vue'
import CondensedProposalPreviewer from '@/components/proposalmaker/CondensedProposalPreviewer'
import VDropZone from '@/components/base/VDropZone'
import VRichText from '@/components/base/VRichText'
import VDynamicList from '@/components/base/VDynamicList'
import VTextField from '@/components/base/VTextField'
import VTextArea from '@/components/base/VTextArea'
import VFileUpload from '@/components/base/VFileUpload'
import VMultiCheckbox from '@/components/base/VMultiCheckbox'
import VMultiselect from '@/components/base/VMultiselect'
import VSelect from '@/components/base/VSelect'
import VDatepicker from '@/components/base/VDatepicker'
import VCheckbox from '@/components/base/VCheckbox'
import VLabel from '@/components/base/VLabel'
import VAlert from '@/components/base/VAlert'
import VLoading from '@/components/base/VLoading'
import VSpinLoader from '@/components/base/VSpinLoader'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import VRichTextRenderer from '@/components/base/VRichTextRenderer'

import {
  mitrexPricingDetails,
  railingPriceTemplate,
  wallPriceTemplate,
  claddingPriceTemplate
} from '@/data/mitrex-pricing-details'
import { mpaSummaryTemplate, dpaSummaryTemplate } from '@/data/mitrex-pricing-summary'
import { 
  mitrexValueVsPrice,
  mpaWallValueVsPrice, 
  mpaRailingValueVsPrice,
  dpaWallValueVsPrice,
  dpaRailingValueVsPrice } from '@/data/mitrex-value-vs-price'
import {
  condensedPriceTemplate,
  budgetSummary,
  materialPriceTemplate
} from '@/data/condensed-pricing-summary'
// import {
//   installDesc, installList
// } from '@/data/condensed-default-text'

import { cladifyItemPriceTemplate } from '@/data/cladify-pricing-item'
import { cladifyServicePriceTemplate } from '@/data/cladify-pricing-service'
import { cladifyPricingSummary } from '@/data/cladify-pricing-summary'
import { mitrexDefaultText } from '@/data/mitrex-default-text'

import { CONDENSED, FULL } from '@/helpers/constants'

import { parseJSON, format } from 'date-fns'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  name: 'ProposalMaker',
  components: {
    PdfPreviewer, VDropZone, VRichText, VDynamicList, VTextField, VTextArea,
    VFileUpload, VMultiCheckbox, VMultiselect, VSelect, VCheckbox, VDatepicker,
    VLabel, VAlert, VFadeTransition, VLoading, VSpinLoader, VRichTextRenderer,
    CondensedProposalPreviewer
  },
  data() {
    return {
      CONDENSED: CONDENSED,
      FULL: FULL,
      proposal: {}, // this is a shallow copy of the state
      proposalLoading: true,
      estimationSummary: null,
      saves: 1,
      richTextRefreshCount: 1,
      parseDate: d => d ? parseJSON(d) : d,
      formatDate: d => d ? format(d, "yyyy-MM-dd HH:mm:ss") : d
    }
  },
  computed: {
    ...mapState("proposalMaker", {
      project: "project",
      proposalFull: "proposal",
      saveCount: "saveCount"
    }),
    ...mapState("rfq", {
      rfq: "rfq",
      rfqLoading: "loading",
      rfqFirstLoad: "firstLoad"
    }),
    editable() {
      return isNaN(new Date(this.proposal.sent_on).getDate()) || this.proposal.sent_on === null
    },
    productBrand() {
      return this.project.crm_object.Product_Brand;
    },
    loadingMessage() {
      return this.proposalLoading ? 'Loading Proposal ...' : 
        this.rfqLoading ? 'Loading RFQ ...' : '';
    },
    imgNameBase() {
      return `prj_${this.$route.params.projectId}_p_${this.$route.params.proposalId}_`;
    },
    filteredRfqs() {
      return this.project.rfqs.filter(d => 
        (!!d.c_estimation && d.c_estimation.is_completed == true 
        || !!d.estimation && d.estimation.is_completed == true) && 
        ((this.proposalFull.proposal_object.proposal_type === CONDENSED 
          && d.rfq_type === CONDENSED)
          || (this.proposalFull.proposal_object.proposal_type !== CONDENSED 
          && d.rfq_type !== CONDENSED))
      );
    },
    estimation({ rfq }) {
      return rfq ? rfq.c_estimation ? rfq.c_estimation : rfq.estimation : null;
    },
    installationMethods({ estimation }) {
      if (estimation && estimation.items && estimation.items.length > 0) {
        let installations = estimation.items.map(
            d => d.installations
          ).flat(1).filter(
            d => d.installation.installation_type_id == 1);

        let res = [];
        installations.forEach(installation => {
          if (res.length == 0 || 
            !res.map(d => d.id).includes(installation.installation_id)) {
            res.push(installation.installation);
          }
        });

        return res;
      } else {
        return []
      }
    },
    materials({ estimation }) {
      if (estimation && estimation.items && estimation.items.length > 0) {
        let materials = estimation.items.map(
            d => d.materials.map(m => ({
              ...m, estimation_item_id: d.id, architecture_name: d.architecture_name
            }))
          ).flat(1).filter(d => [13, 20].includes(d.product_master.product_category_id));

        return materials.map(m => ({
          ...m.product_master, 
          estimation_item_id: m.estimation_item_id, 
          architecture_name: m.architecture_name,
          manual_avg_kw_sqft: m.manual_avg_kw_sqft
        }));
      } else {
        return []
      }
    },
    estimationHasWall({ estimation }) {
      if (this.productBrand == 'Mitrex' && estimation?.estimation_summary?.summary) {
        return estimation.estimation_summary.summary.filter(
          d => d.pricing_summary.includes('solar_wall')).length > 0 ||
          estimation.estimation_summary.irregular_price_by_material
            .filter(d => d.product_brand == 'Mitrex').length > 0;
      } else {
        return false;
      }
    },
    estimationHasRailing({ estimation }) {
      if (this.productBrand == 'Mitrex' && estimation?.estimation_summary?.summary) {
        return estimation.estimation_summary.summary.filter(
          d => d.pricing_summary.includes('railing')
        ).length > 0;
      } else {
        return false;
      }
    },
    estimationHasCladding({ estimation }) {
      if (this.productBrand == 'Mitrex' && estimation?.estimation_summary?.summary) {
        return estimation.estimation_summary.summary.filter(
          d => d.pricing_summary == 'cladding'
        ).length > 0;
      } else {
        return false;
      }
    }
  },
  watch: {
    saveCount() {
      this.saveProposal();
    },
    'proposalFull.proposal_object.name'(newName, oldName) {
      if (
        [oldName, null, undefined, ''].includes(newName) == false && 
        oldName != undefined && this.proposal
      ) {
        this.proposal.name = newName;
        this.saveProposal(false);
      }
    }
  },
  mounted() {
    this.initialize();
  },
  beforeDestroy() {
    this.setRfq(null);
    this.setProject(null);
    this.setProposal(null);
  },
  methods: {
    ...mapActions("proposalMaker", ["getProject"]),
    ...mapActions("rfq", ["getRfq"]),
    ...mapMutations("rfq", ["setFirstLoad", "setRfq"]),
    ...mapMutations("app", ["setNotification"]),
    ...mapMutations("proposalMaker", [
      "setProject", "setProposal", "updateProposalObject"
    ]),
    initialize() {
      if (this.$route.params.project) {
        this.setProject(this.$route.params.project);
        this.processProposal();
      } else {
        this.getProject(this.$route.params.projectId)
          .then(() => {
            this.processProposal();
          })
      }
    },
    processProposal() {
      let proposal = this.project.proposals.filter(
        d => d.id == this.$route.params.proposalId
      )[0];
      this.setProposal(proposal);

      // When we finish getting the project, make a copy of the proposal object
      this.proposal = Object.assign({}, this.proposalFull.proposal_object);

      // Set Quote No.
      if (proposal.quote_number && 
        !proposal.proposal_object.project_info.contact.value["Quote No."]) {
        this.changedQuoteNo(proposal.quote_number);
      }

      this.proposalLoading = false;

      if (this.proposal.project_info.pricing && 
        this.proposal.project_info.pricing.rfq_id) {
        this.getRfq(this.proposal.project_info.pricing.rfq_id).then(() => {
          this.refreshRfq();
        })
      } else {
        this.setFirstLoad(false);
      }
    },
    saveProposal(refresh=true) {
      this.$http.proposal.put(
        this.$route.params.projectId,
        this.$route.params.proposalId,
        { proposal: this.proposal }
      ).then(() => {
        this.updateProposalObject(this.proposal);
        if (refresh) { 
          this.saves += 1;
        }
      })
    },
    fileInput(imageFile) {
      // Create formdata for posting to the back-end
      let formData = new FormData();
      formData.append('proposal_files', imageFile, imageFile.name);

      this.$http.images.post(
        this.$route.params.projectId,
        this.$route.params.proposalId,
        formData,
        { prefix: this.imgNameBase }
      ).then((resp) => {
        this.proposal.project_info.cover_photo.value.file = imageFile;
        this.proposal.project_info.cover_photo.value.url = resp.data.files[0];
        this.saves +=1;
      })
    },
    changedQuoteNo(v) {
      this.proposal.project_info.contact.value["Quote No."] = v;
      this.proposal.project_info.contact.bind.options.forEach(contact => {
        contact["Quote No."] = v;
      })
    },
    changeContact(arr, v) {
      return arr.filter(d => d.Email == v)[0];
    },
    updatedRfq(rfqId) {
      this.getRfq(rfqId).then(() => {
        this.refreshRfq();
        if (this.productBrand === 'Mitrex' && this.editable) {
          this.updateProducts();
        }
        const { rfq } = this;
        if (rfq.rfq_type === CONDENSED) {
          this.proposal.proposal_info.forEach(section => {
            if (section.id === 'quote_type') {
              section.value = rfq.quote_type
              section.bind = {
                ...section.bind, 
                options: [...section.bind.options.filter(o => o.product_brand === this.productBrand)]
              }
            }

            if (section.id === 'material_onsite') {
              section.value = rfq.rfq_material_onsite_date
            }
          })
        }
      })
    },
    updateProducts() {
      const { rfq, estimationHasWall, estimationHasRailing, estimationHasCladding } = this;
      const txt = mitrexDefaultText;
      
      this.proposal.proposal_info.forEach(section => {
        if (section.id === 'supply_install') {
          const supply = 'supply';
          const install = 'install';

          if (rfq.has_supply) {
            if (!section.value.includes(supply)) {
              section.values.push(supply);
            }
          } else {
            if (section.value.includes(supply)) {
              section.value.splice(section.value.indexOf(supply),1)
            }
          }

           if (rfq.has_install) {
            if (!section.value.includes(install)) {
              section.values.push(install);
            }
          } else {
            if (section.value.includes(install)) {
              section.value.splice(section.value.indexOf(install),1)
            }
          }
        }

        if (section.id === 'products') {
          const wall = 'Solar Cladding';
          const railing = 'SolaRail&#153';
          if (estimationHasWall) {
            if (!section.value.includes(wall)) {
              section.value.push(wall);
            }
          } else {
            if (section.value.includes(wall)) {
              section.value.splice(section.value.indexOf(wall),1)
            }
          }

          if (estimationHasRailing) {
            if (!section.value.includes(railing)) {
              section.value.push(railing);
            }
          } else {
            if (section.value.includes(railing)) {
              section.value.splice(section.value.indexOf(railing),1)
            }
          }
        }

        if (section.id === 'quote_details') {
          const wallTxt = txt[section.id].solar_cladding;
          const railingTxt = txt[section.id].solar_railing;
          const claddingTxt = txt[section.id].regular_cladding;

          if (
            !estimationHasWall && 
            section.value.includes(claddingTxt)
          ) {
            section.value = section.value.replace(wallTxt, '')
          }

          if (
            !estimationHasRailing && 
            section.value.includes(railingTxt)
          ) {
            section.value = section.value.replace(railingTxt, '')
          }

          if (
            !estimationHasCladding && section.value.includes(claddingTxt)
          ) {
            section.value = section.value.replace(claddingTxt, '')
          }
        }

        if (['project_scope', 'project_exclusions'].includes(section.id)) {
          const splitWith = '<strong>Solar Railing</strong>'
          const split = section.value.split(splitWith);

          if (!estimationHasWall && split.length === 2 && split[0].includes('<strong>Solar Cladding</strong>')) {
            section.value = `${splitWith}${split[1]}`
          } 
          if (!estimationHasRailing && split.length === 2) {
            section.value = split[0]
          }
        }

        if (section.id === 'quote_type') {
          section.value = rfq.quote_type
        }

        if (section.id === 'material_onsite') {
          section.value = rfq.rfq_material_onsite_date
        }
      })

      this.proposal.proposal_content.forEach(section => {
        if (section.id === 'general_tos') {
          section.subsections.forEach(subsection => {
            if (subsection.id === 'acceptable_tolerances') {
              const splitWith = '<strong>Solar Railing</strong>'
              const split = subsection.value.split(splitWith);
              
              if (!estimationHasWall && split.length === 2 && split[0].includes('<strong>Solar Cladding</strong>')) {
                subsection.value = `${splitWith}${split[1]}`
              } 
              if (!estimationHasRailing && split.length === 2) {
                subsection.value = split[0]
              }
            }
          })
        }
      })

      this.richTextRefreshCount += 1;
    },
    refreshRfq() {
      let needsUpdate = this.rfq && this.editable;

      if (needsUpdate) {
        // Update Pricing Sections
        if (this.rfq.rfq_type === CONDENSED) {
          this.updateCondensedPricing();
        } else if (this.productBrand == 'Mitrex') {
          this.updateMitrexPricing();
          this.updateBattAndInv();
        } else {
          this.updateCladifyPricing();
        }

        // Update Drawing Highlights
        if (this.rfq.drawing_highlights && this.rfq.drawing_highlights.length > 0 && 
          this.rfq.drawing_highlights[0].files.length > 0) {
          this.updateDrawingHighlights();
        }

        // Update Material Images
        if (this.materials.length > 0) {
          this.updateMaterials();
        }

        // Update Installation Methods
        if (this.installationMethods.length > 0) {
          this.updateInstallationMethods();
        }
      }

      // Update RFQ Documents
      if (this.rfq.documents && this.rfq.documents.length > 0) {
        this.updateDocuments();
      }
    },
    updateCondensedPricing() {
      const condensedPriceSummary = JSON.parse(JSON.stringify(condensedPriceTemplate));
      const budgetTotal = JSON.parse(JSON.stringify(budgetSummary));
      this.proposal.proposal_content.forEach((section, i) => {
        if (section.id === 'pricing_summary' && section.type !== 'pricing') {
          this.materials.forEach((material, materialIndex) => {
            let materialPrice = JSON.parse(JSON.stringify(materialPriceTemplate));
            materialPrice.forEach(s => {
              if (s.id === 'header') {
                s.value = `<strong>${material.architecture_name}</strong>`
              }
              if (s.id === 'content') {
                s.display.bind.estimationItemId = material.estimation_item_id;
              }
            })
            if (materialIndex === this.materials.length - 1) {
              materialPrice.splice(2, 0, budgetTotal)
            }
            condensedPriceSummary.subsections.push(materialPrice)
          })
          
          this.proposal.proposal_content.splice(i, 1, condensedPriceSummary);
        }
      })
    },
    updateMitrexPricing() {
      const has_mpa = this.proposal.project_info.pricing.has_mpa_price;
      const has_dpa = this.proposal.project_info.pricing.has_dpa_price;

      // make copies of summaries
      this.updateMitrexPricingSummary(has_mpa, has_dpa);
      this.updateMitrexValueVsPrice(has_mpa, has_dpa);
      // this.updateMitrexPricingDetails(has_mpa, has_dpa);
    },
    updateMitrexPricingSummary(has_mpa, has_dpa) {
      const mpaSummary = JSON.parse(JSON.stringify(mpaSummaryTemplate));
      const dpaSummary = JSON.parse(JSON.stringify(dpaSummaryTemplate));
      const { rfq } = this;
      // const totalMpaPrice = rfq.c_estimation ?
      //   rfq.c_estimation.estimation_summary.summary.map(item => item.price).reduce((a, b) => +a + +b, 0) : null;
      // const totalDpaPrice = rfq.c_estimation ?
      //   rfq.c_estimation.estimation_summary_dpa.summary.map(item => item.price).reduce((a, b) => +a + +b, 0) : null;

      // mpaSummary.forEach(section => {
      //   if (section.value && section.value.includes('{PRICE_BASE_MPA}') && rfq.c_estimation) {
      //     section.value = section.value.replace(
      //       '{PRICE_BASE_MPA}', 
      //       this.$options.filters.currency(totalMpaPrice)
      //     )
      //   }
      // })

      // dpaSummary.forEach(section => {
      //   if (section.value && section.value.includes('{PRICE_BASE_DPA}') && rfq.c_estimation) {
      //     section.value = section.value.replace(
      //       '{PRICE_BASE_DPA}', 
      //       this.$options.filters.currency(totalDpaPrice)
      //     )
      //   }
      // })
      
      this.proposal.proposal_content.forEach((section, i) => {
        if (section.id == 'pricing_summary') {
          if (section.type == 'pricing') {
            let mpaIndex = section.subsections.map(subsection => 
              subsection.map(d => d.content_id).includes('mpa')
            ).indexOf(true);
            let dpaIndex = section.subsections.map(subsection => 
              subsection.map(d => d.content_id).includes('dpa')
            ).indexOf(true);

            if (has_mpa) {
              if (mpaIndex == -1) {
                section.subsections.unshift(mpaSummary);
              } /*else {
                section.subsections.forEach(section => {
                  if (section.value && section.value.includes('{PRICE}')) {
                    section.value = section.value.replace(
                      '{PRICE}', 
                      this.$options.filters.currency(totalMpaPrice)
                    )
                  }
                })
              }*/
            } else {
              if (mpaIndex != -1) {
                section.subsections.splice(mpaIndex, 1);
              }
            }

            if (has_dpa) {
              if (dpaIndex == -1) {
                section.subsections.push(dpaSummary);
              } /*else {
                section.subsections.forEach(section => {
                  if (section.value && section.value.includes('{PRICE}')) {
                    section.value = section.value.replace(
                      '{PRICE}', 
                      this.$options.filters.currency(totalDpaPrice)
                    )
                  }
                })
              }*/
            } else {
              if (dpaIndex != -1) {
                section.subsections.splice(dpaIndex, 1);
              }
            }
          } else {
            let summary = {
              id: 'pricing_summary',
              name: 'Pricing Summary',
              type: 'pricing',
              subsections: []
            }
            if (has_mpa) {
              summary.subsections.unshift(mpaSummary);
            }
            if (has_dpa) {
              summary.subsections.push(dpaSummary);
            }
            this.proposal.proposal_content.splice(i, 1, summary);
          }
        }
      })
    },
    updateMitrexValueVsPrice(has_mpa, has_dpa) {
      const valueVsPrice = JSON.parse(JSON.stringify(mitrexValueVsPrice));
      const mpaRailing = JSON.parse(JSON.stringify(mpaRailingValueVsPrice));
      const mpaWall = JSON.parse(JSON.stringify(mpaWallValueVsPrice));
      const dpaRailing = JSON.parse(JSON.stringify(dpaRailingValueVsPrice));
      const dpaWall = JSON.parse(JSON.stringify(dpaWallValueVsPrice));

      const { estimation, estimationHasWall, estimationHasRailing } = this;

      /* 
      var railingLinearFt, wallSqft;

      //Get linear ft for railing
      if (estimationHasRailing) {
        Object.keys(estimation.estimation_summary).forEach(key => {
          if (key.includes('solar_railing')) {
            railingLinearFt = estimation.estimation_summary[key].map(
              d => d.sum_linear_ft
            ).reduce((a, b) => a + +b, 0);
          }
        });
      }
      mpaRailing.forEach(item => {
        if (item.id == 'content') {
          item.value = item.value.replace(
            '___', 
            this.$options.filters.commasep(railingLinearFt, 0)
          )
        }
      });
      dpaRailing.forEach(item => {
        if (item.id == 'content') {
          item.value = item.value.replace(
            '___', 
            this.$options.filters.commasep(railingLinearFt, 0)
          )
        }
      });
      
      // Get sqft for solar wall
      if (estimationHasWall) {
        wallSqft = estimation.estimation_summary.summary.filter(
          d => ['solar_wall_active', 'solar_wall_non_active'].includes(d.pricing_summary)
        ).map(d => d.measure).reduce((a,b) => a + +b, 0);
      }
      mpaWall.forEach(item => {
        if (item.id == 'content') {
          item.value = item.value.replace(
            '___', 
            this.$options.filters.commasep(wallSqft, 0)
          )
        }
      });
      dpaWall.forEach(item => {
        if (item.id == 'content') {
          item.value = item.value.replace(
            '___', 
            this.$options.filters.commasep(wallSqft, 0)
          )
        }
      });*/
      
      // Update Proposal Object
      this.proposal.proposal_content.forEach((section, i) => {
        if (section.id == 'value_vs_price') {
          if (section.type == 'pricing') {
            
            let mpaWallIndex = section.subsections.map(subsection => 
              subsection.map(d => d.pricing_type).includes('mpa') &&
              subsection.map(d => d.wall_railing).includes('wall')
            ).indexOf(true);

            let mpaRailIndex = section.subsections.map(subsection => 
              subsection.map(d => d.pricing_type).includes('mpa') &&
              subsection.map(d => d.wall_railing).includes('railing')
            ).indexOf(true);

            if (has_mpa) {
              if (estimationHasRailing) {
                if (mpaRailIndex == -1) {
                  section.subsections.unshift(mpaRailing);
                } else {
                  // let contentInd = section.subsections[mpaRailIndex].map(
                  //   d => d.id).indexOf('content');
                  // let newContentInd = mpaRailing.map(d => d.id).indexOf('content');
                  
                  // section.subsections[mpaRailIndex].splice(
                  //   contentInd, 1, mpaRailing[newContentInd]);
                }
              } else {
                if (mpaRailIndex != -1) {
                  section.subsections.splice(mpaRailIndex, 1);
                }
              }
              if (estimationHasWall) {
                if (mpaWallIndex == -1) {
                  section.subsections.unshift(mpaWall);
                } else {
                  // let contentInd = section.subsections[mpaWallIndex].map(
                  //   d => d.id).indexOf('content');
                  // let newContentInd = mpaWall.map(d => d.id).indexOf('content');
                  
                  // section.subsections[mpaWallIndex].splice(
                  //   contentInd, 1, mpaWall[newContentInd]);
                } 
              } else {
                if (mpaWallIndex != -1) {
                  section.subsections.splice(mpaWallIndex, 1);
                }
              }
            } else {
              section.subsections = section.subsections.filter(
                (d, i) => ![mpaWallIndex, mpaRailIndex].includes(i)
              )
            }

            let dpaWallIndex = section.subsections.map(subsection => 
              subsection.map(d => d.pricing_type).includes('dpa') &&
              subsection.map(d => d.wall_railing).includes('wall')
            ).indexOf(true);

            let dpaRailIndex = section.subsections.map(subsection => 
              subsection.map(d => d.pricing_type).includes('dpa') &&
              subsection.map(d => d.wall_railing).includes('railing')
            ).indexOf(true);

            if (has_dpa) {
              if (estimationHasWall) {
                if (dpaWallIndex == -1) {
                  section.subsections.push(dpaWall);
                } else {
                  // let contentInd = section.subsections[dpaWallIndex].map(
                  //   d => d.id).indexOf('content');
                  // let newContentInd = dpaWall.map(d => d.id).indexOf('content');
                  
                  // section.subsections[dpaWallIndex].splice(
                  //   contentInd, 1, dpaWall[newContentInd]);
                }
              } else {
                if (dpaWallIndex != -1) {
                  section.subsections.splice(dpaWallIndex);
                }
              }

              if (estimationHasRailing) {
                if (dpaRailIndex == -1) {
                  section.subsections.push(dpaRailing);
                } else {
                  // let contentInd = section.subsections[dpaRailIndex].map(
                  //   d => d.id).indexOf('content');
                  // let newContentInd = dpaRailing.map(d => d.id).indexOf('content');
                  
                  // section.subsections[dpaRailIndex].splice(
                  //   contentInd, 1, dpaRailing[newContentInd]);
                }
              } else {
                if (dpaRailIndex != -1) {
                  section.subsection.splice(dpaRailIndex);
                }
              }
            } else {
              section.subsections = section.subsections.filter(
                (d, i) => ![dpaWallIndex, dpaRailIndex].includes(i)
              )
            }
          } else {
            if (estimationHasWall) {
              if (has_mpa) {
                valueVsPrice.subsections.push(mpaWall);
              }
              if (has_dpa) {
                valueVsPrice.subsections.push(dpaWall);
              }
            }

            if (estimationHasRailing) {
              if (has_mpa) {
                valueVsPrice.subsections.push(mpaRailing);
              }
              if (has_dpa) {
                valueVsPrice.subsections.push(dpaRailing);
              }
            }
            
            this.proposal.proposal_content.splice(i, 1, valueVsPrice);
          }
        }
      })
    },
    updateMitrexPricingDetails(has_mpa, has_dpa) {
      const pricingDetails = JSON.parse(JSON.stringify(mitrexPricingDetails)),
          railingPrice = JSON.parse(JSON.stringify(railingPriceTemplate)),
          wallPrice = JSON.parse(JSON.stringify(wallPriceTemplate)),
          claddingPrice = JSON.parse(JSON.stringify(claddingPriceTemplate));

      const { estimationHasWall, estimationHasRailing, estimationHasCladding } = this;

      wallPrice.forEach(item => {
        if (item.id == 'content') {
          item.display.bind.hasMpaPrice = has_mpa;
          item.display.bind.hasDpaPrice = has_dpa;
        }
      });

      railingPrice.forEach(item => {
        if (item.id == 'content') {
          item.display.bind.hasMpaPrice = has_mpa;
          item.display.bind.hasDpaPrice = has_dpa;
        }
      });

      this.proposal.proposal_content.forEach((section, i) => {
        if (section.id == 'detailed_pricing') {
          if (section.type == 'pricing') {
            let wallIndex = section.subsections.map(subsection => 
              subsection.map(d => d.display.component).includes('DetailedPricingWall')
            ).indexOf(true);

            let railIndex = section.subsections.map(subsection => 
              subsection.map(d => d.display.component).includes('DetailedPricingRailing')
            ).indexOf(true);

            let claddingIndex = section.subsections.map(subsection => 
              subsection.map(d => d.display.component).includes('DetailedPricingCladding')
            ).indexOf(true);

            if (estimationHasWall) {
              if (wallIndex == -1) {
                section.subsections.push(wallPrice);
              } else {
                section.subsections[wallIndex].forEach(item => {
                  if (item.id == 'content') {
                    item.display.bind.hasMpaPrice = has_mpa;
                    item.display.bind.hasDpaPrice = has_dpa;
                  }
                })
              }
            } else if (!estimationHasWall && wallIndex != -1) {
              section.subsections.splice(wallIndex, 1);
            }

            if (estimationHasRailing) {
              if (railIndex == -1) {
                section.subsections.push(railingPrice);
              } else {
                section.subsections[railIndex].forEach(item => {
                  if (item.id == 'content') {
                    item.display.bind.hasMpaPrice = has_mpa;
                    item.display.bind.hasDpaPrice = has_dpa;
                  }
                })
              }
            } else if (!estimationHasRailing && railIndex != -1) {
              section.subsections.splice(railIndex, 1);
            }

            if (estimationHasCladding && claddingIndex == -1) {
              section.subsections.push(claddingPrice);
            } else if (!estimationHasCladding && claddingIndex != -1) {
              section.subsections.splice(claddingPrice, 1);
            }

          } else {
            if (estimationHasWall) {
              pricingDetails.subsections.push(wallPrice);
            }

            if (estimationHasRailing) {
              pricingDetails.subsections.push(railingPrice);
            }

            if (estimationHasCladding) {
              pricingDetails.subsections.push(claddingPrice);
            }

            this.proposal.proposal_content.splice(i, 1, pricingDetails);
          }
        }
      })
    },
    updateBattAndInv() {
      const has_mpa = this.proposal.project_info.pricing.has_mpa_price;

      this.proposal.proposal_content.forEach(section => {
        if (section.id == 'batt_and_inverter') {
          let battAndInv = this.estimation.estimation_summary.summary.filter(
            d => d.pricing_summary == 'batteries and invertors'
          )[0]
          if (battAndInv && section.bind) {
            section.bind.amount = battAndInv.what_should_be_paid;
            section.bind.discount = battAndInv.what_should_be_paid - battAndInv.price;
            section.bind.hasMpaPrice = has_mpa;
          }
        }
      })
    },
    updateCladifyPricing() {
      // make copies of summaries
      const pricingSummary = Object.assign({}, cladifyPricingSummary);
      const itemPricing = JSON.parse(JSON.stringify(cladifyItemPriceTemplate));
      const servicePricing = JSON.parse(JSON.stringify(cladifyServicePriceTemplate));

      const { estimation } = this;

      // Create the detailed pricing subsections
      let detailedPricing = {
        id: 'detailed_pricing',
        name: 'Detailed Pricing',
        type: 'pricing',
        subsections: []
      }

      estimation.items.forEach(item => {
        let itemData = JSON.parse(JSON.stringify(itemPricing));
        itemData.forEach(itemTemplate => {
          if (itemTemplate.id == 'title') {
            itemTemplate.value = item.architecture_name;
          } else if (itemTemplate.id == 'content') {
            itemTemplate.display.bind.itemId = item.id;
          }
        })
        detailedPricing.subsections.push(itemData);
      })

      detailedPricing.subsections.push(servicePricing);

      this.proposal.proposal_content.forEach((section, i) => {
        if (section.id == 'pricing_summary') {
          if (section.type != 'pricing') {
            this.proposal.proposal_content.splice(i, 1, pricingSummary);
          }
        }

        if (section.id == 'detailed_pricing') {
          if (section.type == 'pricing') {
            let itemIds = section.subsections.map(
              d => d.filter(d => d.id == 'content')
            ).flat(1).map(d => d.display.bind.itemId);

            let newItemIds = estimation.items.map(d => d.id);

            detailedPricing.subsections.forEach(subsection => {
              let itemId = subsection.filter(d => d.id == 'content')[0].display.bind.itemId;
              if (!itemIds.includes(itemId)) {
                section.subsections.push(subsection);
              }
            })

            // section.subsections.forEach((subsection, subsectionIndex) => {
            //   let itemId = subsection.filter(d => d.id == 'content')[0].display.bind.itemId;
            //   if (!newItemIds.includes(itemId)) {
            //     section.subsections.splice(subsectionIndex, 1);
            //   }
            // });

            section.subsections = section.subsections.filter(d => {
              return newItemIds.includes(
                d.filter(d => d.id == 'content')[0].display.bind.itemId
              )
            })
          } else {
            this.proposal.proposal_content.splice(i, 1, detailedPricing);
          }
        }
      })
    },
    updateDocuments() {
      this.proposal.proposal_info.forEach(section => {
        if (section.id == 'documents') {
          // section.bind.rowTemplate = [
          //   { 
          //     label: 'name',
          //     component: 'VTextField',
          //     bind: { disabled: true },
          //     default: ''
          //   },
          //   { 
          //     label: 'received_on',
          //     component: 'VDatepicker',
          //     bind: { disabled: true },
          //     default: null 
          //   },
          //   {
          //     label: 'details',
          //     component: 'VTextField',
          //     bind: {
          //       placeholder: "Enter other details for document"
          //     },
          //     default: ''
          //   }
          // ];

          this.rfq.documents.forEach(document => {
            let index = section.value.map(d => d.id).indexOf(document.id);
            let doc = {
              id: document.id,
              name: document.document_type.document_type_name,
              received_on: this.parseDate(document.received_on),
              details: null
            }

            if (index == -1) {
              section.value.push(doc);
            } else {
              section.value.splice(index, 1, doc);
            }
          });

          section.value = section.value.filter(
            d => this.rfq.documents.map(d => d.id).includes(d.id) || !d.id
          );
        }
      })
    },
    updateMaterials() {
      this.proposal.proposal_content.forEach(section => {
        if (section.id == 'material_and_installation') {
          section.subsections.forEach(subsection => {
            if (subsection.id == 'materials') {
              this.syncMaterials(subsection)
            }
          })
        } else if (section.id === 'materials') {
          this.syncMaterials(section, 'subsection_template', 'subsections')
        }
      })
    },
    syncMaterials(section, templateField='template', valueField='value') {
      const { materials } = this;
      // disable ability to add more images
      if (section.f_bind) {
        section.f_bind['hasAddButton'] = false;
        section.f_bind['hasDeleteButton'] = false;
        section.f_bind['hasVisibilityToggle'] = true;
      } else {
        section.f_bind = {
          hasAddButton: false,
          hasDeleteButton: false,
          hasVisibilityToggle: true
        }
      }

      // disable ability to change file & elevation
      section[templateField].forEach(template => {
        if (template.label == 'image') {
          template.bind.disabled = true;
        }
      })

      // replace placeholders with installation method images
      materials.forEach(material => {
        let index = section[valueField].map(d => d.id).indexOf(material.id);
        const materialType = material.product_brand === 'Mitrex' 
            ? material.product_category_id === 13
              ? 'Mitrex Facing Panel'
              : 'Mitrex Railing'
            : 'Cladify Facing Panel'
        const isRailing = materialType === 'Mitrex Railing'
        const avgKwSqft = material.product_brand === 'Mitrex' && material.manual_avg_kw_sqft
          ? `(${material.manual_avg_kw_sqft}W/${isRailing ? 'LF' : 'SQFT'})`
          : ''

        if (index == -1) {
          section[valueField].push({
            id: material.id,
            material: material.name,
            material_type: materialType,
            text: avgKwSqft,
            image: {
              file: '',
              name: material.filename ? material.filename : 
                `${material.name} image`,
              url: material.image
            },
            visible: true
          })
        } else {
          let old = section[valueField][index];
          let newMaterial = {
            id: material.id,
            material: material.name,
            material_type: materialType,
            text: old.text,
            image: {
              file: '',
              name: material.filename ? material.filename : 
                `${material.installation_name} image`,
              url: material.image
            },
            visible: old.visible
          }
          section[valueField].splice(index, 1, newMaterial);
        }
      })

      section[valueField] = section[valueField].filter(d => 
          materials.map(d => d.id).includes(d.id));
    },
    updateInstallationMethods() {
      this.proposal.proposal_content.forEach(section => {
        if (section.id == 'material_and_installation') {
          section.subsections.forEach(subsection => {
            if (subsection.id == 'install_method') {
              this.syncInstallationMethods(subsection)
            }
          })
        } else if (section.id === 'install_method') {
          this.syncInstallationMethods(section, 'subsection_template', 'subsections')
        }
      })
    },
    syncInstallationMethods(section, templateField='template', valueField='value') {
      const { installationMethods } = this;

      // disable ability to add more images
      if (section.f_bind) {
        section.f_bind.hasAddButton = false;
        section.f_bind.hasDeleteButton = false;
        section.f_bind.hasVisibilityToggle = true;
      } else {
        section.f_bind = {
          hasAddButton: false,
          hasDeleteButton: false,
          hasVisibilityToggle: true
        };
      }

      // disable ability to change file & elevation
      section[templateField].forEach(template => {
        if (template.label == 'image') {
          template.bind.disabled = true;
        }
      })

      // replace placeholders with installation method images
      installationMethods.forEach(installation => {
        let index = section[valueField].map(d => d.id).indexOf(installation.id);

        if (index == -1) {
          section[valueField].push({
            id: installation.id,
            method: installation.installation_name,
            text: this.proposal.proposal_type === CONDENSED ? installation.description : '',
            list: this.proposal.proposal_type === CONDENSED ? installation.desc_list : '',
            image: {
              file: '',
              name: installation.filename ? installation.filename : 
                `${installation.installation_name} image`,
              url: installation.image
            },
            visible: true
          })
        } else {
          let text = section[valueField][index]['text'];
          let list = section[valueField][index]['list'];
          let newInstallation = {
            id: installation.id,
            method: installation.installation_name,
            text: text,
            list: list,
            image: {
              file: '',
              name: installation.filename ? installation.filename : 
                `${installation.installation_name} image`,
              url: installation.image
            },
            visible: true
          }
          section[valueField].splice(index, 1, newInstallation);
        }

        section[valueField] = section[valueField].filter(d => 
          installationMethods.map(d => d.id).includes(d.id));
        
      })
    },
    updateRFQVariables() {
      const { rfq, installationMethods } = this;

      this.proposal.proposal_info.forEach(section => {
        if (section.id == 'quote_details') {
          if (section.value.includes('{TOTAL_SQFT}') && rfq.c_estimation) {
            section.value = section.value.replace(
              '{TOTAL_SQFT}', 
              this.$options.filters.commasep(rfq.c_estimation.total_raw_area, 0)
            )
          } 
          if (section.value.includes('{TOTAL_LFT}') && rfq.c_estimation) {
            section.value = section.value.replace(
              '{TOTAL_LFT}', 
              this.$options.filters.commasep(rfq.c_estimation.total_linear_ft, 0)
            )
          }
        }

        if (section.id === 'system' && section.value.includes('{INSTALLATION_METHODS}')) {
          let methods = ''
          installationMethods.forEach(method => {
            methods += `<li>${method.installation_name}</li>`
          })
          section.value = section.value.replace('{INSTALLATION_METHODS}', methods);
        }

        if (section.id === 'project_scope' && section.value.includes('{INSTALLATION_METHODS}')) {
          let methods = ''
          const noMethods = installationMethods.length;
          installationMethods.forEach((method, i) => {
            methods += `${noMethods > 1 ? i+1 === noMethods ? 'and' : ', ' : ' '}${method.installation_name}`
          })
          section.value = section.value.replace('{INSTALLATION_METHODS}', methods);
        }

        if (section.id === 'pmf' && section.value.includes('{PMF}')) {
          let pmf = ''
          rfq.c_estimation.items.forEach(item => {
            let itemStr = ''
            const facing = item.materials.find(material => material.product_master.product_category_id === 13);
            const backing = item.materials.find(material => material.product_master.product_category_id === 14);
            const railing = item.materials.find(material => material.product_master.product_category_id === 20);
            const installation = item.installations.find(install => install.installation.installation_type_id === 1);
            
            if (facing) {
              itemStr += `<li>${facing.product_master.name}</li>`
            }
            if (backing) {
              itemStr += `<li>${backing.product_master.name}</li>`
            }
            if (railing) {
              itemStr += `<li>${railing.product_master.name}</li>`
            }
            if (installation) {
              itemStr += `<li>${installation.installation.installation_name}</li>`
            }
            if (item.processing.length > 0) {
              item.processing.forEach(processing => {
                itemStr += `<li>${processing.processing.processing_name}</li>`
              })
            }

            itemStr = `<li>${this.productBrand === 'Mitrex' && 'Solar '}
              ${item.architecture_name}<ul>${itemStr}</ul></li>`
            pmf += itemStr
          })

          pmf = `<ul>${pmf}</ul>`

          section.value = section.value.replace('{PMF}', pmf)
        }
      })
    },
    updateDrawingHighlights() {
      const { rfq, proposal } = this;
      if (rfq.drawing_highlights && rfq.drawing_highlights.length > 0 && 
        rfq.drawing_highlights[0].files.length > 0) {
        proposal.proposal_content.forEach(section => {
          if (section.id == 'material_and_installation') {
            section.subsections.forEach(subsection => {
              if (subsection.id == 'drawing_highlight') {
                this.syncDrawingHighlights(subsection);
              }
            })
          } else if (section.id === 'drawing_highlight') {
            this.syncDrawingHighlights(section, 'subsection_template', 'subsections')
          }
        })
      }
    },
    syncDrawingHighlights(section, templateField='template', valueField='value') {
      const { rfq } = this;
      if (!section.f_bind) {
        section.f_bind = {};
      }
      if (section.f_bind.hasAddButton || section.f_bind.hasDeleteButton) {
        // disable ability to add more images
        section.f_bind.hasAddButton = false;
        section.f_bind.hasDeleteButton = false;

        // disable ability to change file & elevation
        section[templateField].forEach(template => {
          if (['elevation', 'image'].includes(template.label)) {
            template.bind.disabled = true;
          }
        })
      }

      // replace placeholders with drawing highlights provided by estimator
      rfq.drawing_highlights[0].files.forEach(highlight => {
        let elevationName = highlight.elevation ? 
          highlight.elevation : highlight.elevation_obj ? 
          highlight.elevation_obj.elevation_name : '';
        let index = section[valueField].map(d => d.id).indexOf(highlight.id);
        if (index == -1) {
          section[valueField].push({
            id: highlight.id,
            elevation: elevationName,
            text: '',
            image: {
              file: '',
              name: highlight.filename ? highlight.filename : `${highlight.elevation} image`,
              url: highlight.image
            }
          })
        } else {
          let text = section[valueField][index]['text'];
          let newHighlight = {
            id: highlight.id,
            elevation: elevationName,
            text: text,
            image: {
              file: '',
              name: highlight.filename ? highlight.filename : `${highlight.elevation} image`,
              url: highlight.image
            }
          }
          section[valueField].splice(index, 1, newHighlight);
        }
      })

      section[valueField] = section[valueField].filter(d => 
        rfq.drawing_highlights[0].files.map(d => d.id).includes(d.id));
    }
  }
}
</script>

<style lang="scss">
@import '../styles/flexbox.scss';
@import '../styles/proposal-maker.scss';
</style>
